/**
 * Какой платёж закрывает расход — когда его записали обычным расходом.
 *
 * Владелец оплатил коммуналку и записал расход «ЖКХ — 8 592,72 ₽»: платёж
 * «ЖКХ» за 10 октября остался неоплаченным, и «Можно тратить» вычитало его
 * второй раз. Здесь — догадка: неоплаченный платёж рядом по сроку (±2
 * недели), чьё название есть в категории или заметке расхода (или та же
 * категория). Решает человек: в форме это включённая строка, которую можно
 * снять, в «Что с платежом?» — кнопка «Это оплата».
 */
import {
  ZERO, addDays, formatMoney, isAfter, isBefore, paymentInstances, sub, unsettledInstances, type Loan, type Money, type PaymentInstance, type PlainDate,
} from '@skoka/core';
import type { AppState, Tx } from './store.js';
import { dayMonth } from './text.js';

const WINDOW = 14;

/** Слова названия: «Связь (интернет)» → связь, интернет. Короткие не считаем. */
const words = (s: string): string[] => s.toLowerCase().replace(/ё/g, 'е')
  .split(/[^a-zа-я0-9]+/).filter((w) => w.length >= 3);

const matches = (state: Pick<AppState, 'obligations' | 'categories'>, obligationId: string, categoryId: string | undefined, text: string): boolean => {
  const o = state.obligations.find((x) => x.id === obligationId);
  if (!o) return false;
  if (o.categoryId && o.categoryId !== 'other' && o.categoryId === categoryId) return true;
  const name = words(o.name);
  if (name.length === 0) return false;
  const cat = state.categories.find((c) => c.id === categoryId)?.name ?? '';
  const hay = new Set(words(`${cat} ${text}`));
  // Все слова названия платежа: «Связь (интернет)» не путается со «Связь (мобильная)».
  return name.every((w) => hay.has(w));
};

/**
 * Обычный расход, похожий на оплату платежа: «ЖКХ — 10 000 ₽», «Няня — 9 000 ₽».
 *
 * Для «обычно в месяц» это не жизнь: платежи вычитаются отдельно, и в
 * «обычно» они вошли бы второй раз. Признак тот же, что у связки оплаты:
 * название платежа в категории или заметке и, у постоянной суммы, почти та
 * же сумма (±10 %).
 */
export function looksLikePayment(
  state: Pick<AppState, 'obligations' | 'categories'>, t: { categoryId?: string; note?: string; amount: Money },
): boolean {
  return state.obligations.some((o) => matches(state, o.id, t.categoryId, t.note ?? '')
    && (o.amountType === 'variable' || Math.abs(t.amount - o.amount) <= o.amount * 0.1));
}

/** Неоплаченные наступления рядом с датой: просроченные и ближайшие. */
function openInstances(state: AppState, date: PlainDate): PaymentInstance[] {
  const late = unsettledInstances({
    obligations: state.obligations, reserves: state.reserves, today: state.today,
    ...(state.trackingStartedAt ? { trackingStartedAt: state.trackingStartedAt } : {}),
  });
  const soon = paymentInstances({
    obligations: state.obligations, reserves: state.reserves, today: state.today,
    from: addDays(date, -WINDOW), to: addDays(date, WINDOW),
  }).filter((i) => !i.settled);
  const seen = new Set<string>();
  return [...late, ...soon].filter((i) => {
    if (seen.has(i.id)) return false;
    seen.add(i.id);
    return !isBefore(i.scheduledDate, addDays(date, -WINDOW - 31)) && !isAfter(i.scheduledDate, addDays(date, WINDOW));
  });
}

/** Платёж, который, похоже, оплачен этим расходом; ближайший по сроку. */
export function paymentFor(
  state: AppState, expense: { date: PlainDate; categoryId?: string; note?: string; amount?: Money },
  /**
   * Сумма любая: форма записи показывает связь строкой, которую видно и можно
   * выключить, — там отдали больше или часть, и связь не должна пропадать.
   * Выписка связывает сама, без строки, и ей нужна почти та же сумма.
   */
  { anyAmount = false }: { anyAmount?: boolean } = {},
): PaymentInstance | null {
  /*
    Постоянная сумма — и сумма должна почти совпасть (±10 %): «Связь и
    интернет — 1 649 ₽» не оплата интернета за 575 ₽. У меняющейся суммы
    (ЖКХ) совпадения не ждём.
  */
  const close = (i: PaymentInstance): boolean => {
    const o = state.obligations.find((x) => x.id === i.obligationId);
    if (anyAmount || !o || o.amountType === 'variable' || expense.amount === undefined || expense.amount <= 0) return true;
    return Math.abs(expense.amount - i.unpaid) <= i.unpaid * 0.1;
  };
  const found = openInstances(state, expense.date)
    .filter((i) => matches(state, i.obligationId, expense.categoryId, expense.note ?? '') && close(i));
  const gap = (i: PaymentInstance) => Math.abs(new Date(i.scheduledDate).getTime() - new Date(expense.date).getTime());
  return found.sort((a, b) => gap(a) - gap(b))[0] ?? null;
}

/** Записанные расходы, которые могут быть оплатой этого наступления. */
export function paymentTxs(state: AppState, inst: PaymentInstance): Tx[] {
  return state.transactions
    .filter((t) => t.type === 'expense' && !t.link
      && !isBefore(t.occurredDate, addDays(inst.scheduledDate, -WINDOW))
      && !isAfter(t.occurredDate, addDays(inst.scheduledDate, WINDOW + 31))
      && matches(state, inst.obligationId, t.categoryId, t.note ?? ''))
    .sort((a, b) => b.occurredDate.localeCompare(a.occurredDate));
}

/** Сколько из оплаты уходит на наступление и сколько по нему останется. */
export interface PayShare { date: PlainDate; take: Money; left: Money }

/**
 * Как разойдутся деньги: сначала это наступление, лишнее — в счёт следующих
 * (16.10.2026). Няне 9 000 ₽ в неделю, отдали 10 000: 9 000 — за эту неделю,
 * 1 000 — в счёт следующей, там останется 8 000. Так же считает и ядро
 * (`carriedInto`); здесь — чтобы сказать это до записи. У меняющейся суммы
 * лишнего нет: такой пришёл счёт.
 */
export function splitPayment(
  state: Pick<AppState, 'obligations' | 'reserves' | 'today' | 'loans'>, obligationId: string, scheduledDate: PlainDate, amount: Money,
): { first: PayShare; next: PayShare[]; over: Money; prepay: Money; loan: Loan | null } {
  const o = state.obligations.find((x) => x.id === obligationId);
  const all = o ? paymentInstances({
    obligations: [o], reserves: state.reserves, today: state.today, from: scheduledDate, to: addDays(scheduledDate, 400),
  }) : [];
  const own = all.find((i) => i.scheduledDate === scheduledDate);
  const unpaid = own ? own.unpaid : amount;
  const take = amount < unpaid ? amount : unpaid;
  const first = { date: scheduledDate, take, left: sub(unpaid, take) };
  let over = amount > unpaid ? sub(amount, unpaid) : ZERO;
  /*
    Кредит: сверх платежа по графику — досрочное погашение, а не аванс на
    следующий месяц (21.10.2026). Оно уменьшает долг и записывается отдельно.
  */
  const loan = loanOf(state, obligationId);
  if (o && over > ZERO && (loan || o.kind === 'loan' || o.kind === 'credit_card_payment')) {
    return { first, next: [], over: ZERO, prepay: over, loan };
  }
  if (!o || o.amountType === 'variable' || over === ZERO) return { first, next: [], over: ZERO, prepay: ZERO, loan };
  const total = over;
  const next: PayShare[] = [];
  for (const i of all) {
    if (over === ZERO || i.scheduledDate <= scheduledDate || i.settled) continue;
    const part = over < i.unpaid ? over : i.unpaid;
    next.push({ date: i.scheduledDate, take: part, left: sub(i.unpaid, part) });
    over = sub(over, part);
  }
  return { first, next, over: total, prepay: ZERO, loan };
}

/** Кредит, чей это платёж по графику, — если долг ещё не погашен. */
export function loanOf(state: Pick<AppState, 'loans'>, obligationId: string): Loan | null {
  return state.loans.find((l) => l.obligationId === obligationId && l.principal > ZERO) ?? null;
}

/** Подсказка в листе оплаты платежа: куда пойдут деньги. Пусто — сказать нечего. */
export function splitNote(split: ReturnType<typeof splitPayment>, today: PlainDate): string {
  const { first, next, over } = split;
  const day = (d: PlainDate) => dayMonth(d, today);
  if (first.take === ZERO) return '';
  if (first.left > ZERO) return `Это часть платежа за ${day(first.date)}: останется доплатить ${formatMoney(first.left)}. Платёж будет ждать остаток.`;
  // Кредит без записи в «Долгах»: досрочное посчитать не из чего.
  if (split.prepay > ZERO) return `${formatMoney(first.take)} закроют платёж за ${day(first.date)}, ${formatMoney(split.prepay)} — сверх платежа по графику.`;
  if (over === ZERO) return '';
  const head = `${formatMoney(first.take)} закроют платёж за ${day(first.date)}`;
  if (next.length === 0) return `${head}, ${formatMoney(over)} — сверх него: следующего платежа по расписанию нет.`;
  if (next.length === 1) {
    const [n] = next as [PayShare];
    return `${head}, ${formatMoney(n.take)} — в счёт следующего, ${day(n.date)}. ${n.left > ZERO ? `Там останется ${formatMoney(n.left)}.` : 'Он будет оплачен целиком.'}`;
  }
  return `${head}, ${formatMoney(over)} — в счёт следующих: ${next.map((n) => `${day(n.date)} — ${n.left > ZERO ? formatMoney(n.take) : 'целиком'}`).join(', ')}.`;
}

/** Та же подсказка короче — строкой под суммой в записи расхода. */
export function splitLine(split: ReturnType<typeof splitPayment>, today: PlainDate, amount: Money): string {
  const { first, next, over } = split;
  const day = (d: PlainDate) => dayMonth(d, today);
  if (amount === ZERO) return `за ${day(first.date)} · ${formatMoney(first.left)}`;
  if (first.left > ZERO) return `часть за ${day(first.date)} · останется ${formatMoney(first.left)}`;
  if (split.prepay > ZERO) return `${formatMoney(first.take)} — за ${day(first.date)} · ${formatMoney(split.prepay)} — досрочно`;
  if (over === ZERO) return `за ${day(first.date)} — целиком`;
  const rest = next.length === 1 ? `в счёт ${day(next[0]!.date)}` : next.length > 1 ? 'в счёт следующих' : 'сверх платежа';
  return `${formatMoney(first.take)} — за ${day(first.date)} · ${formatMoney(over)} — ${rest}`;
}
